import { useHeroBackgrounds } from '@/hooks/useHeroBackgrounds'
import { motion, useReducedMotion } from 'framer-motion'
import { ArrowDown, MapPin } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import SmartImage from './SmartImage'

/**
 * Single hero card — the premium tour operator brand slide. Carries the
 * canonical page <h1> and primary CTA in the first-paint HTML. Strings are
 * curated here (with the same i18n keys as before under the `hero` namespace)
 * so they never drift with the DB; the background comes from the cached home
 * hero bg so first paint is never bare.
 *
 * ponytail: was a 6-slide activity rotator; repositioned to one operator card,
 * so the rotation/swipe/dots machinery was removed rather than left dormant.
 */
const OPERATOR = {
  key: 'operator',
  // Two-tone display: the place name stays clear (cream) and the rest of the
  // heading is accent-coloured. `headingPlace` is the localized place token we
  // split `heading` on, so it works whatever the word order (start/mid/end).
  heading: 'Premium Tour Operator In Vang Vieng',
  headingPlace: 'Vang Vieng',
  tagline:
    'Guided day tours and adventures across Vang Vieng and Laos: rock climbing, trekking, hot air balloon, lake cruises and electric enduro, all run by our own local team.',
  eyebrow: 'Guided · Local · Premium',
  findUsLabel: 'Find Us',
} as const

const HeroActivityRotator: React.FC = () => {
  const reduceMotion = useReducedMotion()
  const { t } = useTranslation('hero')
  const { data: heroBg } = useHeroBackgrounds()
  const image = heroBg?.home

  const scrollDown = () =>
    window.scrollBy({
      top: window.innerHeight * 0.92,
      behavior: reduceMotion ? 'auto' : 'smooth',
    })

  const tr = (field: keyof typeof OPERATOR) =>
    t(`${OPERATOR.key}.${field}`, { defaultValue: OPERATOR[field] })

  // Two-tone heading: place name clear, the rest accent. Split on the place
  // token wherever it falls; if it's missing, render the whole line clear.
  const heading = tr('heading')
  const place = tr('headingPlace')
  const at = heading.indexOf(place)
  const headingParts =
    at < 0
      ? [{ text: heading, accent: false }]
      : [
          { text: heading.slice(0, at), accent: true },
          { text: place, accent: false },
          { text: heading.slice(at + place.length), accent: true },
        ].filter((p) => p.text)

  return (
    <div className="absolute inset-0 w-full h-full">
      {/* Background image + readability overlays */}
      <div className="absolute inset-0 w-full h-full overflow-hidden bg-garage-dark">
        {image && (
          <SmartImage
            src={image}
            alt={tr('heading')}
            className="absolute inset-0 w-full h-full"
            loading="eager"
            fetchPriority="high"
          />
        )}
        <div className="absolute inset-0 bg-garage-texture opacity-10 pointer-events-none" />
        <div className="absolute inset-0 bg-garage-mud-splatter opacity-5 pointer-events-none" />
        <div className="absolute inset-0 bg-gradient-to-t from-black/75 via-black/35 to-black/45 pointer-events-none" />
      </div>

      {/* Content */}
      <div className="relative z-10 min-h-dvh-screen flex items-center justify-center">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 sm:py-16 w-full">
          <motion.div
            initial={{ opacity: 0, y: reduceMotion ? 0 : 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{
              duration: reduceMotion ? 0.2 : 0.45,
              ease: 'easeOut',
            }}
            className="text-center w-full"
          >
            <h1 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-black mb-3 sm:mb-4 leading-[1.1] text-center text-garage-cream tracking-tight max-w-4xl mx-auto">
              <span className="block drop-shadow-garage">
                {headingParts.map((p, i) => (
                  <span
                    key={i}
                    className={p.accent ? 'text-garage-accent' : ''}
                  >
                    {p.text}
                  </span>
                ))}
              </span>
            </h1>

            <p className="text-sm sm:text-base md:text-lg text-garage-cream/90 font-semibold mb-3 max-w-2xl mx-auto px-2 leading-snug text-center drop-shadow-garage">
              {tr('tagline')}
            </p>

            <p className="text-xs sm:text-sm md:text-base mb-6 sm:mb-8 text-garage-accent font-bold tracking-wider uppercase text-center drop-shadow-garage">
              {tr('eyebrow')}
            </p>

            {/* Primary CTA — jumps to the map section near the page bottom */}
            <div className="flex justify-center px-4 sm:px-0">
              <motion.div
                whileHover={reduceMotion ? {} : { scale: 1.08 }}
                whileTap={{ scale: 0.95 }}
                transition={{ duration: 0.2 }}
                className="relative group"
              >
                <div className="absolute -inset-3 bg-gradient-to-r from-garage-accent via-garage-primary to-garage-red rounded-xl blur-xl opacity-60 group-hover:opacity-100 transition-opacity duration-200" />
                <div className="absolute -inset-1 bg-gradient-to-r from-garage-accent/50 via-garage-primary/50 to-garage-red/50 rounded-lg blur-sm opacity-40 group-hover:opacity-80 transition-opacity duration-150" />
                <a
                  href="#find-us"
                  className="relative btn-garage-primary !text-white text-lg sm:text-xl px-8 sm:px-12 py-4 sm:py-5 font-bold border-3 border-garage-accent hover:border-garage-red transition-all duration-150 shadow-2xl hover:shadow-garage-xl inline-flex items-center gap-3"
                >
                  <MapPin className="h-6 w-6 relative z-10" />
                  <span className="relative z-10">{tr('findUsLabel')}</span>
                </a>
              </motion.div>
            </div>
          </motion.div>
        </div>
      </div>

      {/* Scroll-down cue — subtle accent chevron, gentle bounce */}
      <motion.button
        type="button"
        onClick={scrollDown}
        aria-label="Scroll down"
        initial={{ opacity: 0 }}
        animate={
          reduceMotion ? { opacity: 0.7 } : { opacity: 0.7, y: [0, 8, 0] }
        }
        transition={
          reduceMotion
            ? { duration: 0.4 }
            : { y: { duration: 1.6, repeat: Infinity, ease: 'easeInOut' } }
        }
        whileHover={{ opacity: 1 }}
        // ponytail: x lives in the motion transform, not a Tailwind class —
        // framer's animated `y` overwrites `transform`, killing -translate-x-1/2.
        style={{ x: '-50%' }}
        className="absolute bottom-5 left-1/2 z-20 text-garage-accent hover:text-garage-red transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-garage-accent/60 rounded-full p-1"
      >
        <ArrowDown className="h-11 w-11 drop-shadow-garage" strokeWidth={2.5} />
      </motion.button>
    </div>
  )
}

export default HeroActivityRotator
